import React, { useState } from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalFonts } from '../../styles/GlobalFonts';
import { fourPracticaLocales } from '../../locales/firstScreen';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

const Container = styled.div`
  background: #151515;
  min-height: 100vh;
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 100px;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 0 10px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: min(110px, 15vh);
  margin: 50px 0 30px;
  padding: min(13px, 2vh);
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  /* Обеспечиваем видимость контента поверх оверлеев */
  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: clamp(40px, 5vw, 34px);
  font-weight: 600;
  margin-bottom: min(8px, 1.2vh);
  margin-top: min(1px, 0.2vh);
  line-height: 100%;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  text-align: center;
  white-space: pre-line;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin: 0 10px;
  gap: 10px;
  padding: 0;
`;

const QuizButton = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 18px 21px;
  border-radius: 12px;
  border: 2px solid
    ${({ selected }) => (selected ? '#8C3FFF' : 'rgba(180, 180, 180, 0.2)')};
  background: none;
  transition: all 0.2s ease;
`;

const CircleIndicator = styled.div<{ selected: boolean; number?: number }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid
    ${({ selected }) => (selected ? '#8C3FFF' : 'rgba(181, 181, 181, 0.2)')};
  background: ${({ selected }) => (selected ? '#8C3FFF' : 'transparent')};
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ selected }) => (selected ? '#FFFFFF' : '#FFFFFF')};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const ButtonText = styled.span`
  flex: 1;
  color: #ffffff;
  font-size: 13px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  text-align: left;
`;

const CheckButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px 21px;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: none;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;
  width: calc(100% - 30px);
  margin: 0 15px;

  &:disabled {
    color: rgba(255, 255, 255, 0.2);
    pointer-events: none;
  }
`;

const FooterUi = styled.div`
  width: 100%;
  background: linear-gradient(180deg, #15151500, #151515);
  padding: 44px 10px 26px;
  margin-top: auto;
  margin-bottom: 80px;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)<{ success?: boolean }>`
  background: ${({ success }) => (success ? '#8C3FFF' : '#151515')};
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 30px 20px;
  margin: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  color: #ffffff;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0 0 24px 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const ModalButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  text-align: center;
  padding: 18px 21px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  background: none;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalContentVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    transition: { duration: 0.3, ease: 'easeIn' },
  },
};

interface Props {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onNavigateToTheory?: () => void;
  onNavigateToPractica2?: () => void;
  toHome?: () => void;
}

const FourPractica1: React.FC<Props> = ({
  onSwipeLeft,
  onSwipeRight,
  onNavigateToTheory,
  onNavigateToPractica2,
  toHome,
}) => {
  const [selectedButtons, setSelectedButtons] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = fourPracticaLocales[lang]?.[1] || {};

  // Для совместимости с текущей логикой, оставим id как есть
  const correctOrder = lang === 'ru' ? [5, 2, 4, 1, 3] : [5, 2, 4, 1, 3]; // если порядок отличается для en, скорректируйте

  // Тексты шагов для локализации
  const buttonTexts = {
    ru: [
      'ЗАБЛОКИРОВАТЬ LP-ТОКЕНЫ В СМАРТ-КОНТРАКТЕ',
      'ПОСТАВИТЬ ЛИКВИДНОСТЬ В ВЫБРАННЫЙ ПУЛ',
      'НАЧАТЬ ПОЛУЧАТЬ НАГРАДЫ!',
      'АВТОМАТИЧЕСКИ ПОЛУЧИТЬ LP-ТОКЕНЫ',
      'ПРИОБРЕСТИ ОБА ТОКЕНА В ПАРЕ',
    ],
    en: [
      'LOCK LP TOKENS IN THE SMART CONTRACT',
      'PROVIDE LIQUIDITY TO THE SELECTED POOL',
      'START RECEIVING REWARDS!',
      'AUTOMATICALLY RECEIVE LP TOKENS',
      'ACQUIRE BOTH TOKENS IN THE PAIR',
    ],
  };

  const buttons = buttonTexts[lang].map((text, idx) => ({
    id: idx + 1,
    text,
  }));

  const handleButtonClick = (id: number) => {
    setSelectedButtons((prev) => {
      if (prev.includes(id)) {
        return prev.filter((btnId) => btnId !== id);
      }
      return [...prev, id];
    });
  };

  const checkAnswer = () => {
    const isCorrect =
      selectedButtons.length === correctOrder.length &&
      selectedButtons.every((btn, index) => btn === correctOrder[index]);

    setIsSuccess(isCorrect);
    setShowModal(true);
  };

  const resetQuiz = () => {
    setSelectedButtons([]);
    setShowModal(false);
  };

  const handleTheoryClick = () => {
    if (onNavigateToTheory) {
      setShowModal(false);
      setSelectedButtons([]);
      onNavigateToTheory();
    }
  };

  const handleNextPractica = () => {
    if (onNavigateToPractica2) {
      setShowModal(false);
      onNavigateToPractica2();
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
  });

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <HomeButton onClick={toHome}>
          <HomeButtonIcon src="/assets/homeIconPurple.svg" />
        </HomeButton>
        <Header>
          <Title>{t.title}</Title>
          <TheoryButton>{t.practica}</TheoryButton>
        </Header>
        <QuestionBlock>
          <QuestionText>{t.question}</QuestionText>
        </QuestionBlock>
        <Description>{t.description}</Description>
        <ButtonContainer>
          {buttons.map((button) => (
            <QuizButton
              key={button.id}
              selected={selectedButtons.includes(button.id)}
              onClick={() => handleButtonClick(button.id)}
            >
              <ButtonText>{button.text}</ButtonText>
              <CircleIndicator
                selected={selectedButtons.includes(button.id)}
                number={selectedButtons.indexOf(button.id) + 1}
              >
                {selectedButtons.includes(button.id)
                  ? selectedButtons.indexOf(button.id) + 1
                  : ''}
              </CircleIndicator>
            </QuizButton>
          ))}
        </ButtonContainer>
        <FooterUi>
          <CheckButton
            onClick={checkAnswer}
            disabled={selectedButtons.length === 0}
          >
            {lang === 'ru' ? 'ПРОВЕРИТЬ' : 'CHECK'}
          </CheckButton>
        </FooterUi>
        <Footer
          practica
          textLeft={t.back}
          onSwipeLeft={onSwipeLeft}
          onSwipeRight={onSwipeRight}
        />

        <AnimatePresence>
          {showModal && (
            <ModalOverlay
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
            >
              <ModalContent
                success={isSuccess}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalContentVariants}
              >
                <CloseButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    resetQuiz();
                  }}
                />
                <ModalTitle>
                  {isSuccess
                    ? lang === 'ru'
                      ? 'Отлично!'
                      : 'Great!'
                    : lang === 'ru'
                      ? 'Упс!'
                      : 'Oops!'}
                </ModalTitle>
                <ModalText>
                  {isSuccess
                    ? lang === 'ru'
                      ? 'Вы всё сделали правильно! Давайте теперь проследим путь ваших токенов и наград.'
                      : 'You did everything right! Now let’s trace the path of your tokens and rewards.'
                    : lang === 'ru'
                      ? 'Это неправильный порядок. Попробуем еще раз?'
                      : "That's the wrong order. Try again?"}
                </ModalText>
                {isSuccess ? (
                  <ModalButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNextPractica();
                    }}
                  >
                    {lang === 'ru' ? 'ВПЕРЁД' : 'CONTINUE'}
                  </ModalButton>
                ) : (
                  <>
                    <ModalButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleTheoryClick();
                      }}
                    >
                      {lang === 'ru'
                        ? 'Как начать фармить?'
                        : 'How to start farming?'}
                    </ModalButton>
                    <ModalButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        resetQuiz();
                      }}
                    >
                      {lang === 'ru' ? 'Назад' : 'Back'}
                    </ModalButton>
                  </>
                )}
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </Container>
    </div>
  );
};

export default FourPractica1;
